import { useMemo } from 'react';
import { useQuery, useMutation, queryCache } from 'react-query';

import type {
    TeacherReserveActivity,
    TeacherReserveAward,
    TeacherReserveParticipant,
} from '@edu-frontend/modules/src/features/teacherReserve';
import {
    getTeacherReserveActivities,
    getTeacherReserveAwards,
    getTeacherReserveParticipant,
    postTeacherReserveMentor,
    postAssignTeacherReserveAwardPromocode,
    postTeacherReservePromocode,
    getTeacherReserveReferrals,
    postLinkMeAsReferral,
} from '@yandex-int/k-common/client/api';
import { showNetworkError } from 'platform-components';

import { getUniqueQueryKeys } from 'utils/getUniqueQueryKey';

import { filterAndSortActivities, sortAwards } from './utils';

const queryKeys = getUniqueQueryKeys(['activities', 'awards', 'participant', 'referrals']);

const onError = (error: unknown) => {
    console.error(error);
    showNetworkError(error);
};

/** Получить список активностей */
export const useTeacherReserveActivities = () => {
    const result = useQuery([queryKeys.activities], () => getTeacherReserveActivities<TeacherReserveActivity>());

    // Здесь же сортируем активности и фильтруем те, что не должны отображаться
    result.data = useMemo(() => result.data && filterAndSortActivities(result.data), [result.data]);

    return result;
};

/** Получить список наград */
export const useTeacherReserveAwards = () => {
    const result = useQuery([queryKeys.awards], () => getTeacherReserveAwards<TeacherReserveAward[]>());

    // Здесь же сортируем награды
    result.data = useMemo(() => result.data && sortAwards(result.data), [result.data]);

    return result;
};

/**
 * Получить профиль участника кадрового резерва
 * @param dontSendRequest не посылать запрос - результат будет соответствовать участнику, который ничего не делал (0 баллов)
 */
export function useTeacherReserveParticipant(dontSendRequest?: boolean) {
    return useQuery(
        [queryKeys.participant, !dontSendRequest], // отрицание чтобы undefined и false получили одинаковый ключ
        (): Promise<TeacherReserveParticipant> =>
            dontSendRequest
                ? Promise.resolve<TeacherReserveParticipant>({
                      id: 0,
                      level: 0,
                      pointsAfterLvlUp: null,
                      commonPoints: 0,
                      canBeMentor: false,
                      activeReferralCount: 0,
                      rank: Infinity,
                      referrerId: null,
                      referrerLinkId: '',
                      schoolName: '',
                      referrals: [],
                      rating: { leaderboard: [], diffToTop: null },
                  })
                : getTeacherReserveParticipant<TeacherReserveParticipant>(),
        { staleTime: 2000 }
    );
}

/** Сделать пользователя ментором кадрового резерва */
export function usePostTeacherReserveMentor() {
    return useMutation(postTeacherReserveMentor, {
        onSuccess: () => {
            queryCache.invalidateQueries([queryKeys.activities]);
            queryCache.invalidateQueries([queryKeys.awards]);
            queryCache.invalidateQueries([queryKeys.participant]);
        },
        onError,
    });
}

/** Сгенерировать промокод пользователю для награды */
export function useAssignTeacherReservePromocode() {
    return useMutation(postAssignTeacherReserveAwardPromocode, {
        onSuccess: () => queryCache.invalidateQueries([queryKeys.awards]),
        onError,
    });
}

/** Использовать промокод */
export function usePostTeacherReservePromocode() {
    return useMutation(postTeacherReservePromocode, {
        onSuccess: () => {
            // промокоды кадрового резерва могут открыть новую награду / обновить прогресс по активности
            queryCache.invalidateQueries([queryKeys.activities]);
            queryCache.invalidateQueries([queryKeys.awards]);
        },
        onError,
    });
}

export function useTeacherReserveReferrals() {
    return useQuery(
        [queryKeys.referrals],
        (): Promise<TeacherReserveParticipant> => getTeacherReserveReferrals<TeacherReserveParticipant>(),
        { staleTime: 2000 }
    );
}

export function useTeacherReserveReferralInvite() {
    return useMutation((referrerLinkId: string) => postLinkMeAsReferral(referrerLinkId), {
        onError,
    });
}
